import { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  size?: 'md' | 'lg' | 'xl';
  /** Tailwind width class e.g. 'w-[420px]'. Takes precedence over size. */
  width?: string;
  side?: 'left' | 'right';
}

export function Drawer({ open, onClose, title, children, size = 'lg', width, side = 'right' }: DrawerProps) {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  if (!open) return null;

  const widthClass = width
    ? width
    : { md: 'max-w-md', lg: 'max-w-2xl', xl: 'max-w-4xl' }[size];

  const isRight = side === 'right';
  const justifyClass = isRight ? 'justify-end' : 'justify-start';
  const borderClass = isRight ? 'border-l' : 'border-r';
  const animateClass = isRight ? 'slide-in-from-right' : 'slide-in-from-left';

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex ${justifyClass} bg-black/50 backdrop-blur-xs transition-opacity`}
      onClick={onClose}
    >
      <div
        className={`w-full ${widthClass} h-full bg-white dark:bg-card ${borderClass} border-gray-200 dark:border-border shadow-2xl flex flex-col overflow-hidden animate-in ${animateClass} duration-200`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-border px-6 py-4 bg-white dark:bg-card">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-foreground truncate">{title}</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:text-muted-foreground dark:hover:bg-muted dark:hover:text-foreground transition-colors"
            aria-label="Close drawer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6 flex-1 overflow-y-auto bg-white dark:bg-card">{children}</div>
      </div>
    </div>,
    document.body
  );
}
